import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { PlusCircle, BookOpen } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/lib/context/auth-context";
import { supabase } from "@/integrations/supabase/client";
import { Course, StateRequirements } from "./types";
import { CourseCard } from "./components/CourseCard";
import { StatsRow } from "./components/StatsRow";
import { DashboardHeader } from "../../components/DashboardHeader";

const CourseList = ({ 
  courses, 
  onStatusChange,
  onDelete,
  onAttendance
}: {
  courses: Course[];
  onStatusChange: (courseId: string, newStatus: string) => void;
  onDelete: (courseId: string) => void;
  onAttendance: (courseId: string) => void;
}) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {courses.map((course, index) => (
        <CourseCard 
          key={course.id} 
          course={course} 
          index={index}
          onStatusChange={onStatusChange}
          onDelete={onDelete}
          onAttendance={onAttendance}
        />
      ))}
    </div>
  );
};

const AdminCourses = () => {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [courses, setCourses] = useState<Course[] | null>(null);

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const { data, error } = await supabase
          .from('courses')
          .select('*')
          .order('created_at', { ascending: false });

        if (error) {
          console.error("Error fetching courses:", error);
          toast({
            title: "Error",
            description: "Failed to load courses. Please try again.",
            variant: "destructive",
          });
          return;
        }

        // Type casting to ensure database values match expected Course type
        const typedCourses = data.map(course => ({
          ...course,
          event_type: course.event_type as "live" | "self_paced",
          status: course.status as "draft" | "published" | "archived" | "cancelled",
          description: course.description as string | null,
          max_participants: course.max_participants as number | null,
          venue_details: course.venue_details as string | null,
          start_date: course.start_date as string | null,
          end_date: course.end_date as string | null,
          target_audience: course.target_audience as "teachers" | "administrators" | "support_staff" | "all",
          // Convert the JSON state_requirements to the proper StateRequirements type
          state_requirements: course.state_requirements as unknown as StateRequirements,
        }));

        setCourses(typedCourses);
      } catch (error: any) {
        console.error("Unexpected error fetching courses:", error);
        toast({
          title: "Error",
          description: error.message || "Failed to load courses. Please try again.",
          variant: "destructive",
        });
      }
    };

    fetchCourses();
  }, [toast]);

  const handleStatusChange = async (courseId: string, newStatus: string) => {
    try {
      const { error } = await supabase
        .from('courses')
        .update({ status: newStatus })
        .eq('id', courseId);

      if (error) {
        console.error("Error updating course status:", error);
        toast({
          title: "Error",
          description: "Failed to update course status. Please try again.",
          variant: "destructive",
        });
        return;
      }

      setCourses((prevCourses) =>
        prevCourses ? prevCourses.map((course) =>
            course.id === courseId ? { ...course, status: newStatus as "draft" | "published" | "archived" | "cancelled" } : course
          ) : []
      );

      toast({
        title: "Success",
        description: "Course status updated successfully!",
      });
    } catch (error: any) {
      console.error("Unexpected error updating course status:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to update course status. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleDeleteCourse = async (courseId: string) => {
    try {
      const { error } = await supabase
        .from('courses')
        .delete()
        .eq('id', courseId);

      if (error) {
        console.error("Error deleting course:", error);
        toast({
          title: "Error",
          description: "Failed to delete course. Please try again.",
          variant: "destructive",
        });
        return;
      }

      setCourses((prevCourses) =>
        prevCourses ? prevCourses.filter((course) => course.id !== courseId) : []
      );

      toast({
        title: "Success",
        description: "Course deleted successfully!",
      });
    } catch (error: any) {
      console.error("Unexpected error deleting course:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to delete course. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleAttendance = async (courseId: string) => {
    navigate(`/admin/courses/${courseId}/attendance`);
  };

  return (
    <div className="w-full px-2 sm:px-4 py-2 space-y-2">
      <DashboardHeader
        title="Course Management"
        subtitle="Create and manage CEU courses and events"
        action={
        <Button 
          onClick={() => navigate('/admin/courses/create')}
            className="gap-1 bg-[#0f8f8c] hover:bg-[#12706a] text-white font-section text-xs px-3 py-1 rounded-full shadow transition-all duration-200"
            size="sm"
            style={{ boxShadow: '0 1px 6px 0 rgba(15,143,140,0.08)' }}
        >
            <PlusCircle className="h-4 w-4" />
            Create Course
        </Button>
        }
      >
        <StatsRow courses={courses} />
      </DashboardHeader>
      {/* Course List Management */}
      <CourseList 
        courses={courses || []} 
        onStatusChange={handleStatusChange}
        onDelete={handleDeleteCourse}
        onAttendance={handleAttendance}
      />
    </div>
  );
};

export default AdminCourses;
